import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/auth/AuthContext";
import { usePublishProgress } from "./Context";
import { isListingUnavailable } from "./types";
import type { PlatformStatus, PublishBatchView, PublishJobView } from "./types";

/** Roles allowed to retry teammates' failed jobs. Brokers / members can
 *  still retry their own — see ``canRetry`` in ``JobRow``. Mirrors the
 *  backend gate in ``_require_job_retry``. */
const RETRY_OTHERS_ROLES = ["executive_manager", "operations_manager", "lead_broker"] as const;

/** Stop / pause / resume a single in-flight pipeline. Returns the
 *  server's reported new state; the panel UI re-reads via the polling
 *  watcher in ``Context.tsx`` so we don't need to optimistically
 *  mutate here — keeps a single source of truth. */
async function _jobAction(jobRunId: string, action: "stop" | "pause" | "resume") {
  try {
    await api(`/api/job-runs/${jobRunId}/${action}`, { method: "POST" });
    return null;
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      // 403 → no permission, 409 → worker already gone. Both are
      // valid "nothing to do" outcomes; bubble the message up so the
      // row can flash a tiny tooltip if we want to later.
      return err.message || `${action} failed`;
    }
    return `${action} failed`;
  }
}

const PLATFORM_DOT: Record<PlatformStatus, string> = {
  pending: "bg-slate-600",
  running: "bg-accent-400 animate-pulse",
  done: "bg-emerald-400",
  error: "bg-rose-400",
  skipped: "bg-slate-700",
};

const PLATFORM_TEXT: Record<PlatformStatus, string> = {
  pending: "text-slate-500",
  running: "text-accent-200",
  done: "text-emerald-300",
  error: "text-rose-300",
  skipped: "text-slate-600",
};

function formatElapsed(ms: number): string {
  if (ms < 1000) return "0s";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs.toString().padStart(2, "0")}s`;
}

/** Compact start-time label for a job row: just the clock time for a job
 *  started today, with a short date prefix for older ones. The full
 *  timestamp lives in the avatar's hover tooltip. */
function formatStartedAt(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

/** Full, locale-formatted timestamp for the avatar hover tooltip. */
function fullTimestamp(ms: number): string {
  return new Date(ms).toLocaleString();
}

/** Progress-bar fill colour per platform status. Mirrors ``PLATFORM_DOT``
 *  but without the running pulse — a pulsing bar fill reads as a glitch. */
const PLATFORM_BAR: Record<PlatformStatus, string> = {
  pending: "bg-slate-600",
  running: "bg-accent-400",
  done: "bg-emerald-400",
  error: "bg-rose-400",
  skipped: "bg-slate-700",
};

/** One portal's half of a publish, rendered as its own row: status dot,
 *  label, progress bar, status text, an open-listing link when it landed,
 *  and — when it failed — the reason plus a Retry button that re-runs
 *  ONLY this portal (the other half is left untouched, and the retry is
 *  spawned as its own independent job). This is what makes MyHome and
 *  SS.ge feel like separate jobs even though one backend run drives both. */
function PlatformRow({
  platform,
  job,
  onRetry,
  retryBusy,
  retryError,
  canRetry,
}: {
  platform: "myhome" | "ss";
  job: PublishJobView;
  onRetry: () => void;
  retryBusy: boolean;
  retryError: string | null;
  canRetry: boolean;
}) {
  const isMh = platform === "myhome";
  const label = isMh ? "MyHome" : "SS.ge";
  const status = isMh ? job.mhStatus : job.ssStatus;
  if (status === "skipped") return null;

  const url = isMh ? job.mhUrl : job.ssUrl;
  const percent = isMh ? job.mhPercent : job.ssPercent;
  // Per-platform reason, falling back to the job's last message for a
  // whole-pipeline failure that never produced a platform-specific one.
  const errText =
    ((isMh ? job.mhError : job.ssError) || "").trim() ||
    (status === "error" ? (job.lastMessage || "").trim() : "");

  // A cancelled job freezes its portals mid-flight — render them as a
  // neutral "stopped" rather than a pulsing "running" that never resolves.
  const stopped =
    job.state === "cancelled" &&
    (status === "running" || status === "pending");

  const pct = status === "done" ? 100 : percent ?? 0;
  const dotCls = stopped ? "bg-slate-500" : PLATFORM_DOT[status];
  const textCls = stopped ? "text-slate-500" : PLATFORM_TEXT[status];
  const barCls = stopped ? "bg-slate-600" : PLATFORM_BAR[status];
  const statusLabel = stopped
    ? "stopped"
    : status === "done"
      ? "done"
      : status === "error"
        ? "failed"
        : status === "running"
          ? `${percent ?? 0}%`
          : "queued";

  return (
    <div className="rounded border border-white/5 bg-white/[0.015] px-2 py-1.5">
      <div className="flex items-center gap-2">
        <span
          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotCls}`}
        />
        <span
          className={`text-[11px] font-semibold w-[52px] flex-shrink-0 ${textCls}`}
        >
          {label}
        </span>
        <div className="flex-1 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className={`h-full ${barCls} transition-[width] duration-500 ease-out`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span
          className={`text-[10px] tabular-nums w-9 text-right flex-shrink-0 ${textCls}`}
        >
          {statusLabel}
        </span>
        {status === "done" && url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-accent-400 hover:text-accent-200 text-[12px] leading-none flex-shrink-0"
            title={`Open the published listing on ${label}`}
          >
            ↗
          </a>
        ) : (
          <span className="w-3 flex-shrink-0" />
        )}
      </div>
      {status === "error" && (
        <div className="mt-1.5 rounded border border-rose-500/20 bg-rose-500/[0.06] p-1.5">
          {errText && (
            <div className="text-[10.5px] text-rose-200 leading-snug whitespace-pre-wrap break-words line-clamp-3">
              {errText}
            </div>
          )}
          {canRetry && (
            <button
              type="button"
              className="mt-1 px-2 py-[3px] rounded border border-accent-400/40 bg-accent-500/[0.1] hover:bg-accent-500/[0.18] text-accent-100 text-[10px] font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onRetry}
              disabled={retryBusy}
              title={`Re-publish only ${label} — the other portal is left untouched`}
            >
              {retryBusy ? "Retrying…" : `↻ Retry ${label}`}
            </button>
          )}
          {retryError && (
            <div className="mt-1 text-[10px] text-amber-200 leading-snug">
              {retryError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Terminal "source listing removed" notice. Rendered in place of the
 *  per-platform rows when the source listing was deleted/deactivated on
 *  the portal (see ``isListingUnavailable``). It's not a failure the
 *  broker can fix, so this carries no Retry — just the message in a calm
 *  sky tone plus Copy, distinct from the red error styling. */
function UnavailableNotice({ message }: { message: string }) {
  const [copied, setCopied] = useState(false);
  const text =
    (message || "").trim() ||
    "This listing was deleted or deactivated on the source portal and can no longer be republished.";

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked in some browsers / iframes — ignore */
    }
  }

  return (
    <div className="mt-1 rounded border border-sky-500/25 bg-sky-500/[0.07] p-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-300 mb-0.5 flex items-center gap-1">
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        Listing removed
      </div>
      <div className="text-[11px] text-sky-100/90 leading-snug whitespace-pre-wrap break-words">
        {text}
      </div>
      <div className="mt-1.5 flex items-center text-[10px]">
        <button
          type="button"
          className="text-sky-300/80 hover:text-sky-200 underline-offset-2 hover:underline ml-auto"
          onClick={copy}
          title="Copy this message to your clipboard"
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function rowPercent(job: PublishJobView): number | null {
  if (job.state === "done") return 100;
  const useSs = job.useSs && job.ssStatus !== "skipped";
  const mh = job.mhPercent;
  const ss = job.ssPercent;
  if (!useSs) return mh;
  if (mh == null && ss == null) return null;
  if (mh == null) return ss;
  if (ss == null) return mh;
  return Math.min(mh, ss);
}

function JobRow({ job }: { job: PublishJobView }) {
  const { dismiss, register } = usePublishProgress();
  const { user } = useAuth();
  // Per-platform retry state: which portal's retry is mid-flight, and the
  // last retry error tagged with the portal it belongs to so it renders
  // under the right row.
  const [retryBusy, setRetryBusy] = useState<"myhome" | "ss" | null>(null);
  const [retryError, setRetryError] = useState<
    { platform: "myhome" | "ss"; message: string } | null
  >(null);

  // Retry visibility mirrors the backend gate (``_require_job_retry``):
  // the user who spawned the job can always retry it; manager-and-above
  // can retry teammates' jobs. Allowed on any terminal job (notably
  // ``done`` — the partial-success case).
  const isElevated =
    !!user?.role &&
    (RETRY_OTHERS_ROLES as readonly string[]).includes(user.role);
  const isTerminal =
    job.state === "done" || job.state === "error" || job.state === "cancelled";
  const canRetryPlatform = isTerminal && (!job.isForeign || isElevated);

  // The source listing was deleted/deactivated — terminal and benign,
  // shown as a single sky notice instead of two portal rows.
  const unavailable = isListingUnavailable(job);

  // "Who ran this": the agent the JobRun is attributed to. For the
  // viewer's own jobs that agent is the viewer, so fall back to the
  // signed-in user when the spawning page passed no explicit label.
  const ownJob = !job.isForeign;
  const viewerName = user?.full_name || user?.email || null;
  const agentName =
    job.agentLabel ||
    (ownJob ? viewerName : null) ||
    "Unknown";
  const agentAvatar =
    job.agentAvatarUrl ||
    (ownJob && !job.agentLabel ? user?.avatar_url ?? null : null);

  // Who launched the job (clicked Publish / Retry). Foreign jobs carry it
  // from the bulk-jobs API; page-spawned jobs were launched by the
  // signed-in viewer. Only surfaced separately when it differs from the
  // agent — for a self-publish the two are one and the same.
  const triggeredByName =
    job.triggeredByName || (ownJob ? viewerName : null);
  const triggeredByAvatar =
    job.triggeredByAvatarUrl || (ownJob ? user?.avatar_url ?? null : null);
  const showTriggeredBy =
    !!triggeredByName && triggeredByName !== agentName;

  const elapsed =
    job.state === "running" ? Date.now() - job.startedAt : null;

  async function onRetryPlatform(platform: "myhome" | "ss") {
    if (retryBusy) return;
    setRetryBusy(platform);
    setRetryError(null);
    try {
      const r = await api<{ job_id: string; job_run_id: string }>(
        `/api/job-runs/${job.jobRunId}/retry-platform`,
        { method: "POST", json: { platform } },
      );
      // Spawn the retry as its own row — its own job_id, progress and
      // retry. The original row stays: the other portal's listing is
      // still live and worth seeing.
      register({
        jobId: r.job_id,
        jobRunId: r.job_run_id,
        origin: job.origin,
        title: job.title,
        sourceUrl: job.sourceUrl,
        agentLabel: job.agentLabel,
        agentAvatarUrl: job.agentAvatarUrl,
        // The viewer clicked Retry — they're the launcher of this re-run,
        // whoever the original agent was.
        triggeredByName: user?.full_name || user?.email || null,
        triggeredByAvatarUrl: user?.avatar_url ?? null,
        useSs: platform === "ss",
        isForeign: !!job.isForeign,
        onlyPlatform: platform,
      });
    } catch (err: unknown) {
      let msg = "Retry failed";
      if (err instanceof ApiError) {
        msg = err.message || msg;
        const body = err.body as
          | { detail?: { message?: string } | string }
          | null
          | undefined;
        if (body && typeof body === "object" && body.detail) {
          if (typeof body.detail === "object" && body.detail.message) {
            msg = body.detail.message;
          } else if (typeof body.detail === "string") {
            msg = body.detail;
          }
        }
      }
      setRetryError({ platform, message: msg });
    } finally {
      setRetryBusy(null);
    }
  }

  // Which portal rows to render. A MyHome-only job has no SS row; a
  // per-platform retry leaves the untouched portal ``skipped``.
  const showMh = job.mhStatus !== "skipped";
  const showSs = job.useSs && job.ssStatus !== "skipped";

  return (
    <div className="px-3.5 py-2.5 hover:bg-white/[0.02] transition">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {/* header — origin badge + listing title */}
          <div className="flex items-center gap-1.5">
            <span
              className={`text-[9px] font-semibold uppercase tracking-wider px-1 py-[1px] rounded border ${
                job.origin === "bulk"
                  ? "border-accent-500/40 bg-accent-500/10 text-accent-300"
                  : "border-slate-500/40 bg-slate-500/10 text-slate-300"
              }`}
            >
              {job.origin}
            </span>
            <span className="text-[12.5px] font-medium text-slate-100 leading-snug truncate">
              {job.title || job.sourceUrl}
            </span>
          </div>

          {/* meta — who it published as, who launched it (names + full
              time on hover), and when it started */}
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Avatar
              size="xs"
              url={agentAvatar}
              name={agentName}
              className="!w-[18px] !h-[18px]"
            />
            <span
              className="text-[10.5px] text-slate-400 truncate max-w-[150px]"
              title={`Published as ${agentName} · ${fullTimestamp(job.startedAt)}`}
            >
              {agentName}
            </span>
            {showTriggeredBy && (
              <span className="flex items-center gap-1 text-[10px] text-slate-500 min-w-0">
                <span className="text-slate-600">· launched by</span>
                <Avatar
                  size="xs"
                  url={triggeredByAvatar}
                  name={triggeredByName}
                  className="!w-[15px] !h-[15px]"
                />
                <span
                  className="truncate max-w-[120px]"
                  title={`Launched by ${triggeredByName}`}
                >
                  {triggeredByName}
                </span>
              </span>
            )}
            <span className="text-[10px] text-slate-500 tabular-nums flex-shrink-0">
              · {formatStartedAt(job.startedAt)}
            </span>
            {elapsed !== null && (
              <span className="text-[10px] text-slate-500 tabular-nums flex-shrink-0">
                · {formatElapsed(elapsed)}
              </span>
            )}
          </div>

          {/* body — a sky notice for a removed listing, otherwise one row
              per portal so MyHome and SS.ge track independently */}
          {unavailable ? (
            <UnavailableNotice message={job.lastMessage} />
          ) : (
            <div className="mt-2 space-y-1">
              {showMh && (
                <PlatformRow
                  platform="myhome"
                  job={job}
                  onRetry={() => onRetryPlatform("myhome")}
                  retryBusy={retryBusy === "myhome"}
                  retryError={
                    retryError?.platform === "myhome"
                      ? retryError.message
                      : null
                  }
                  canRetry={canRetryPlatform}
                />
              )}
              {showSs && (
                <PlatformRow
                  platform="ss"
                  job={job}
                  onRetry={() => onRetryPlatform("ss")}
                  retryBusy={retryBusy === "ss"}
                  retryError={
                    retryError?.platform === "ss" ? retryError.message : null
                  }
                  canRetry={canRetryPlatform}
                />
              )}
            </div>
          )}

          {job.state === "running" && !unavailable && (
            <div className="mt-1.5 text-[10.5px] text-slate-400 truncate">
              {job.paused
                ? "Paused — pipeline will resume on next checkpoint"
                : job.stopRequested
                  ? "Stopping…"
                  : job.lastMessage}
            </div>
          )}
          {job.state === "cancelled" && (
            <div className="mt-1.5 text-[10.5px] text-slate-400 truncate">
              {job.lastMessage || "Cancelled"}
            </div>
          )}
        </div>
        {job.state === "running" && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {!job.paused ? (
              <button
                type="button"
                onClick={() => _jobAction(job.jobRunId, "pause")}
                disabled={job.stopRequested}
                className="text-slate-500 hover:text-slate-200 disabled:opacity-30 disabled:hover:text-slate-500 px-1 py-1 rounded hover:bg-white/5 transition"
                aria-label="Pause"
                title="Pause"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="5" width="4" height="14" rx="0.8" />
                  <rect x="14" y="5" width="4" height="14" rx="0.8" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => _jobAction(job.jobRunId, "resume")}
                disabled={job.stopRequested}
                className="text-accent-300 hover:text-accent-200 disabled:opacity-30 px-1 py-1 rounded hover:bg-white/5 transition"
                aria-label="Resume"
                title="Resume"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="7,4 20,12 7,20" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => _jobAction(job.jobRunId, "stop")}
              disabled={job.stopRequested}
              className="text-slate-500 hover:text-rose-300 disabled:opacity-30 disabled:hover:text-slate-500 px-1 py-1 rounded hover:bg-white/5 transition"
              aria-label="Stop"
              title={job.stopRequested ? "Stopping…" : "Stop"}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="1" />
              </svg>
            </button>
          </div>
        )}
        {(job.state === "done" ||
          job.state === "error" ||
          job.state === "cancelled") && (
          <button
            type="button"
            onClick={() => dismiss(job.jobId)}
            className="text-slate-600 hover:text-slate-200 px-1 py-1 rounded hover:bg-white/5 transition flex-shrink-0"
            aria-label="Dismiss"
            title="Dismiss"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/** One row for a whole bulk-publish batch — replaces per-listing detail
 *  with a single aggregate bar + running/done/failed counts, per the
 *  scaling problem those per-listing rows caused on a big bulk (hundreds
 *  of independent watchers, hundreds of concurrent requests). Pause /
 *  resume / stop act on every still-live job in the batch at once. */
function BatchRow({ batch }: { batch: PublishBatchView }) {
  const { pauseBatch, resumeBatch, stopBatch, dismissBatch } =
    usePublishProgress();
  const settled = batch.done + batch.failed;
  const pct = batch.total ? Math.round((settled / batch.total) * 100) : 0;
  const isRunning = batch.running > 0;
  const activeCount = Math.max(0, batch.running - batch.paused);
  const elapsed = isRunning ? Date.now() - batch.createdAt : null;

  function onStop() {
    if (
      !window.confirm(
        `Stop ${batch.running} still-running publish${batch.running === 1 ? "" : "es"} in this batch?`,
      )
    ) {
      return;
    }
    stopBatch(batch.batchId);
  }

  return (
    <div className="px-3.5 py-2.5 hover:bg-white/[0.02] transition">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-semibold uppercase tracking-wider px-1 py-[1px] rounded border border-accent-500/40 bg-accent-500/10 text-accent-300">
              batch
            </span>
            <span className="text-[12.5px] font-medium text-slate-100 leading-snug truncate">
              {batch.total} listing{batch.total === 1 ? "" : "s"}
              {batch.useSs ? " · MyHome + SS.ge" : " · MyHome"}
            </span>
          </div>

          <div className="mt-2 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className={`h-full transition-[width] duration-500 ease-out ${
                !isRunning && batch.failed > 0
                  ? "bg-amber-400"
                  : !isRunning
                    ? "bg-emerald-400"
                    : "bg-accent-400"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10.5px] tabular-nums">
            <span className="text-accent-300">{batch.running} running</span>
            <span className="text-slate-600">·</span>
            <span className="text-emerald-300">{batch.done} done</span>
            <span className="text-slate-600">·</span>
            <span className="text-rose-300">{batch.failed} failed</span>
            {batch.paused > 0 && (
              <>
                <span className="text-slate-600">·</span>
                <span className="text-slate-400">{batch.paused} paused</span>
              </>
            )}
            {elapsed !== null && (
              <span className="text-slate-500 ml-auto flex-shrink-0">
                {formatElapsed(elapsed)}
              </span>
            )}
          </div>
        </div>
        {isRunning ? (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={() => pauseBatch(batch.batchId)}
                className="text-slate-500 hover:text-slate-200 px-1 py-1 rounded hover:bg-white/5 transition"
                aria-label="Pause batch"
                title={`Pause ${activeCount} running job${activeCount === 1 ? "" : "s"}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="5" width="4" height="14" rx="0.8" />
                  <rect x="14" y="5" width="4" height="14" rx="0.8" />
                </svg>
              </button>
            )}
            {batch.paused > 0 && (
              <button
                type="button"
                onClick={() => resumeBatch(batch.batchId)}
                className="text-accent-300 hover:text-accent-200 px-1 py-1 rounded hover:bg-white/5 transition"
                aria-label="Resume batch"
                title={`Resume ${batch.paused} paused job${batch.paused === 1 ? "" : "s"}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="7,4 20,12 7,20" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={onStop}
              className="text-slate-500 hover:text-rose-300 px-1 py-1 rounded hover:bg-white/5 transition"
              aria-label="Stop batch"
              title="Stop every still-running job in this batch"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" rx="1" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => dismissBatch(batch.batchId)}
            className="text-slate-600 hover:text-slate-200 px-1 py-1 rounded hover:bg-white/5 transition flex-shrink-0"
            aria-label="Dismiss"
            title="Dismiss"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── Floating widget ───────────────────────────
 * The publish-progress UI is a single floating widget with three modes
 * the user cycles through, each persisted so it survives reloads:
 *   • "icon"     — a small launcher bubble (like a support-chat widget)
 *                  with a badge for the running / failed count.
 *   • "compact"  — a slim card: roll-up stats + what's in flight.
 *   • "expanded" — a wide (~50vw) panel with the full per-job detail.
 * The widget renders nothing at all when there are no jobs. */

type PanelMode = "icon" | "compact" | "expanded";

const MODE_KEY = "agencygrid.publish_progress.mode.v1";

function loadMode(): PanelMode {
  try {
    const v = window.localStorage.getItem(MODE_KEY);
    if (v === "icon" || v === "compact" || v === "expanded") return v;
  } catch {
    /* ignore */
  }
  return "compact";
}

interface Stats {
  total: number;
  running: number;
  done: number;
  failed: number;
  cancelled: number;
  /** Terminal jobs whose source listing was deleted/deactivated. Not a
   *  failure — surfaced in its own muted "removed" group, never counted
   *  toward ``failed`` so the panel doesn't shout red for them. */
  unavailable: number;
  settled: number;
  /** Completion 0-100 — settled jobs over total. */
  pct: number;
}

/** Roll a job list up into the headline counters every mode shows. A
 *  ``done`` job that still carries a failed platform counts as failed,
 *  so a partial publish never masquerades as a clean success. A
 *  removed-listing job (``isListingUnavailable``) is split out into
 *  ``unavailable`` instead of ``failed`` — it's expected portal state,
 *  not a publish failure. */
function computeStats(jobs: PublishJobView[]): Stats {
  let running = 0;
  let done = 0;
  let failed = 0;
  let cancelled = 0;
  let unavailable = 0;
  for (const j of jobs) {
    if (j.state === "running") running++;
    else if (j.state === "cancelled") cancelled++;
    else if (isListingUnavailable(j)) unavailable++;
    else if (
      j.state === "error" ||
      j.mhStatus === "error" ||
      j.ssStatus === "error"
    ) {
      failed++;
    } else done++;
  }
  const total = jobs.length;
  const settled = done + failed + cancelled + unavailable;
  return {
    total,
    running,
    done,
    failed,
    cancelled,
    unavailable,
    settled,
    pct: total ? Math.round((settled / total) * 100) : 0,
  };
}

/** Fold bulk-publish batches' aggregate counts into the headline stats
 *  the panel already computes from individual jobs. Batches don't track
 *  ``cancelled`` / ``unavailable`` at this granularity — a stopped or
 *  removed-listing job inside a batch is just counted under ``failed``. */
function statsWithBatches(base: Stats, batchList: PublishBatchView[]): Stats {
  if (batchList.length === 0) return base;
  let running = base.running;
  let done = base.done;
  let failed = base.failed;
  let total = base.total;
  let settled = base.settled;
  for (const b of batchList) {
    running += b.running;
    done += b.done;
    failed += b.failed;
    total += b.total;
    settled += b.done + b.failed;
  }
  return {
    ...base,
    running,
    done,
    failed,
    total,
    settled,
    pct: total ? Math.round((settled / total) * 100) : 0,
  };
}

const ICON_EXPAND = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const ICON_COLLAPSE = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 14 10 14 10 20" />
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const ICON_MINIMIZE = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

function IconButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-shrink-0 text-slate-400 hover:text-slate-100 hover:bg-white/10 rounded-md p-1.5 transition"
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function StatusDot({ running, failed }: { running: boolean; failed: boolean }) {
  const cls = running
    ? "bg-accent-400 animate-pulse"
    : failed
      ? "bg-rose-400"
      : "bg-emerald-400";
  return <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${cls}`} />;
}

/** Card wrapper for the compact / expanded modes — dark glass styling
 *  plus a one-shot fade-in so switching modes feels smooth. */
function WidgetShell({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setShown(true));
    return () => window.cancelAnimationFrame(id);
  }, []);
  return (
    <div
      role="status"
      aria-live="polite"
      className={`card border border-white/10 bg-ink-900/95 backdrop-blur-md shadow-2xl shadow-black/60 overflow-hidden origin-bottom-right transition-all duration-200 ease-out ${
        shown
          ? "opacity-100 translate-y-0 scale-100"
          : "opacity-0 translate-y-3 scale-95"
      } ${className ?? ""}`}
    >
      {children}
    </div>
  );
}

/* ── mode 1: collapsed status pill ──
 * A slim dark-glass pill, not a round launcher bubble — the round
 * gradient bubble with a paper-plane glyph read as a support-chat
 * widget. The pill shows a status dot + a terse live summary and
 * expands to the compact card on click. */

function IconBubble({ stats, onOpen }: { stats: Stats; onOpen: () => void }) {
  const running = stats.running > 0;
  const failed = stats.failed > 0;
  // Status dot mirrors the panel's vocabulary: pulsing accent while
  // publishing, rose when something needs attention, emerald when all
  // settled.
  const dotCls = running
    ? "bg-accent-400 animate-pulse"
    : failed
      ? "bg-rose-400"
      : "bg-emerald-400";
  const label = running
    ? `${stats.running} · ${stats.pct}%`
    : failed
      ? `${stats.failed} failed`
      : "All done";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center gap-1.5 rounded-full border border-white/10 bg-ink-900/95 backdrop-blur-md pl-2.5 pr-3 py-1.5 shadow-xl shadow-black/50 hover:bg-ink-800/95 transition"
      aria-label="Open publish progress"
      title={
        running
          ? `${stats.running} publish${stats.running === 1 ? "" : "es"} running`
          : "Publish progress"
      }
    >
      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dotCls}`} />
      {running && (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-accent-300 flex-shrink-0"
        >
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      )}
      <span className="text-[11.5px] font-medium text-slate-100 tabular-nums whitespace-nowrap leading-none">
        {label}
      </span>
    </button>
  );
}

/* ── mode 2: compact summary ── */

function StatPill({
  count,
  label,
  tone,
}: {
  count: number;
  label: string;
  tone: "accent" | "emerald" | "rose" | "sky";
}) {
  const tones = {
    accent: "border-accent-500/30 bg-accent-500/[0.08] text-accent-200",
    emerald: "border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-200",
    rose: "border-rose-500/30 bg-rose-500/[0.08] text-rose-200",
    sky: "border-sky-500/30 bg-sky-500/[0.08] text-sky-200",
  } as const;
  return (
    <div
      className={`flex-1 rounded-lg border px-2 py-1.5 text-center ${tones[tone]}`}
    >
      <div className="text-[15px] font-bold tabular-nums leading-none">
        {count}
      </div>
      <div className="text-[9px] uppercase tracking-wider mt-1 opacity-80">
        {label}
      </div>
    </div>
  );
}

function MiniJobLine({ job }: { job: PublishJobView }) {
  const pct = rowPercent(job) ?? 0;
  return (
    <div className="flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full bg-accent-400 animate-pulse flex-shrink-0" />
      <span className="text-[11px] text-slate-300 truncate flex-1 min-w-0">
        {job.title || job.sourceUrl}
      </span>
      <div className="w-12 h-1 rounded-full bg-white/[0.07] overflow-hidden flex-shrink-0">
        <div
          className="h-full bg-accent-400 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-slate-500 tabular-nums w-8 text-right flex-shrink-0">
        {pct}%
      </span>
    </div>
  );
}

function CompactPanel({
  own,
  foreign,
  batchList,
  onMinimize,
  onExpand,
}: {
  own: PublishJobView[];
  foreign: PublishJobView[];
  batchList: PublishBatchView[];
  onMinimize: () => void;
  onExpand: () => void;
}) {
  const { clearFinished } = usePublishProgress();
  const all = useMemo(() => [...own, ...foreign], [own, foreign]);
  const s = statsWithBatches(computeStats(all), batchList);
  const active = useMemo(
    () =>
      all
        .filter((j) => j.state === "running")
        .sort((a, b) => b.startedAt - a.startedAt),
    [all],
  );

  const headline =
    s.running > 0
      ? `${s.running} publish${s.running === 1 ? "" : "es"} running`
      : s.failed > 0
        ? `${s.failed} publish${s.failed === 1 ? "" : "es"} need attention`
        : "All publishes done";

  return (
    <WidgetShell className="w-[336px] max-w-[calc(100vw-2rem)]">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-white/5">
        <StatusDot running={s.running > 0} failed={s.failed > 0} />
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-slate-100 leading-tight truncate">
            {headline}
          </div>
          <div className="text-[10px] text-slate-500 tabular-nums">
            {s.total} listing{s.total === 1 ? "" : "s"}
            {batchList.length > 0 &&
              ` · ${batchList.length} batch${batchList.length === 1 ? "" : "es"}`}
            {own.length > 0 &&
              foreign.length > 0 &&
              ` · ${own.length} mine · ${foreign.length} team`}
          </div>
        </div>
        <IconButton onClick={onExpand} label="Expand to full view">
          {ICON_EXPAND}
        </IconButton>
        <IconButton onClick={onMinimize} label="Minimize">
          {ICON_MINIMIZE}
        </IconButton>
      </div>

      <div className="flex gap-1.5 px-3 pt-3">
        <StatPill count={s.running} label="Running" tone="accent" />
        <StatPill count={s.done} label="Done" tone="emerald" />
        <StatPill count={s.failed} label="Failed" tone="rose" />
        {s.unavailable > 0 && (
          <StatPill count={s.unavailable} label="Removed" tone="sky" />
        )}
      </div>

      <div className="px-3.5 pt-3">
        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
          <span>Overall progress</span>
          <span className="tabular-nums text-slate-400">
            {s.settled}/{s.total}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className={`h-full transition-[width] duration-500 ease-out ${
              s.running === 0 && s.failed > 0
                ? "bg-amber-400"
                : s.running === 0
                  ? "bg-emerald-400"
                  : "bg-accent-400"
            }`}
            style={{ width: `${s.pct}%` }}
          />
        </div>
      </div>

      <div className="px-3.5 py-3">
        {active.length > 0 ? (
          <>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1.5">
              In progress
            </div>
            <div className="space-y-1.5">
              {active.slice(0, 4).map((j) => (
                <MiniJobLine key={j.jobId} job={j} />
              ))}
            </div>
            {active.length > 4 && (
              <button
                type="button"
                onClick={onExpand}
                className="mt-1.5 text-[10.5px] text-accent-300 hover:text-accent-200"
              >
                +{active.length - 4} more — open full view
              </button>
            )}
          </>
        ) : (
          <div className="text-[11px] text-slate-500 text-center py-1.5">
            {s.failed > 0
              ? "Nothing running — open the full view to retry failed jobs."
              : "All caught up — every publish finished."}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 px-3.5 py-2 border-t border-white/5">
        <button
          type="button"
          onClick={onExpand}
          className="text-[11px] font-medium text-accent-300 hover:text-accent-200 transition"
        >
          Open full view →
        </button>
        <div className="flex-1" />
        {s.settled > 0 && (
          <button
            type="button"
            onClick={clearFinished}
            className="text-[11px] text-slate-500 hover:text-slate-200 transition"
            title="Remove every finished job from the panel"
          >
            Clear finished
          </button>
        )}
      </div>
    </WidgetShell>
  );
}

/* ── mode 3: expanded full detail ── */

function JobSection({
  title,
  kind,
  jobs,
}: {
  title: string;
  kind: "own" | "foreign";
  jobs: PublishJobView[];
}) {
  const [collapsed, setCollapsed] = useState(false);
  const sorted = useMemo(
    () =>
      [...jobs].sort((a, b) => {
        // running first, then real errors, then removed listings, then
        // settled (done / cancelled) — removed jobs sink below failures
        // since they need no action.
        const rank = (j: PublishJobView) =>
          j.state === "running"
            ? 0
            : isListingUnavailable(j)
              ? 2
              : j.state === "error"
                ? 1
                : 3;
        const d = rank(a) - rank(b);
        return d !== 0 ? d : b.startedAt - a.startedAt;
      }),
    [jobs],
  );
  if (jobs.length === 0) return null;
  const s = computeStats(jobs);

  return (
    <section>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="sticky top-0 z-10 w-full flex items-center gap-2 px-4 py-2 bg-ink-900/95 backdrop-blur border-b border-white/5 hover:bg-white/[0.03] transition"
      >
        {kind === "foreign" && (
          <span className="text-[8.5px] font-semibold uppercase tracking-wider px-1 py-[1px] rounded border border-sky-500/40 bg-sky-500/10 text-sky-300">
            team
          </span>
        )}
        <span className="text-[11.5px] font-semibold text-slate-200">
          {title}
        </span>
        <span className="text-[10px] text-slate-500 tabular-nums">
          {jobs.length}
        </span>
        <div className="flex-1" />
        <span className="text-[10px] tabular-nums">
          {s.running > 0 && (
            <span className="text-accent-300">{s.running} running</span>
          )}
          {s.running > 0 && (s.done > 0 || s.failed > 0) && (
            <span className="text-slate-600"> · </span>
          )}
          {s.done > 0 && (
            <span className="text-emerald-300">{s.done} done</span>
          )}
          {s.done > 0 && s.failed > 0 && (
            <span className="text-slate-600"> · </span>
          )}
          {s.failed > 0 && (
            <span className="text-rose-300">{s.failed} failed</span>
          )}
          {s.unavailable > 0 &&
            (s.running > 0 || s.done > 0 || s.failed > 0) && (
              <span className="text-slate-600"> · </span>
            )}
          {s.unavailable > 0 && (
            <span className="text-sky-300">{s.unavailable} removed</span>
          )}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-slate-500 transition-transform ${
            collapsed ? "-rotate-90" : ""
          }`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {!collapsed && (
        <div className="divide-y divide-white/5">
          {sorted.map((j) => (
            <JobRow key={j.jobId} job={j} />
          ))}
        </div>
      )}
    </section>
  );
}

/** Mirrors ``JobSection`` but for aggregate batches — sorted newest
 *  first, collapsible, with its own running/done/failed roll-up in the
 *  header. */
function BatchSection({ batchList }: { batchList: PublishBatchView[] }) {
  const [collapsed, setCollapsed] = useState(false);
  const sorted = useMemo(
    () => [...batchList].sort((a, b) => b.createdAt - a.createdAt),
    [batchList],
  );
  if (batchList.length === 0) return null;
  const running = batchList.reduce((n, b) => n + b.running, 0);
  const done = batchList.reduce((n, b) => n + b.done, 0);
  const failed = batchList.reduce((n, b) => n + b.failed, 0);

  return (
    <section>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="sticky top-0 z-10 w-full flex items-center gap-2 px-4 py-2 bg-ink-900/95 backdrop-blur border-b border-white/5 hover:bg-white/[0.03] transition"
      >
        <span className="text-[11.5px] font-semibold text-slate-200">
          Bulk publishes
        </span>
        <span className="text-[10px] text-slate-500 tabular-nums">
          {batchList.length}
        </span>
        <div className="flex-1" />
        <span className="text-[10px] tabular-nums">
          {running > 0 && <span className="text-accent-300">{running} running</span>}
          {running > 0 && (done > 0 || failed > 0) && (
            <span className="text-slate-600"> · </span>
          )}
          {done > 0 && <span className="text-emerald-300">{done} done</span>}
          {done > 0 && failed > 0 && <span className="text-slate-600"> · </span>}
          {failed > 0 && <span className="text-rose-300">{failed} failed</span>}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-slate-500 transition-transform ${
            collapsed ? "-rotate-90" : ""
          }`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {!collapsed && (
        <div className="divide-y divide-white/5">
          {sorted.map((b) => (
            <BatchRow key={b.batchId} batch={b} />
          ))}
        </div>
      )}
    </section>
  );
}

function ExpandedPanel({
  own,
  foreign,
  batchList,
  onMinimize,
  onCompact,
}: {
  own: PublishJobView[];
  foreign: PublishJobView[];
  batchList: PublishBatchView[];
  onMinimize: () => void;
  onCompact: () => void;
}) {
  const { clearFinished } = usePublishProgress();
  const all = useMemo(() => [...own, ...foreign], [own, foreign]);
  const s = statsWithBatches(computeStats(all), batchList);

  return (
    <WidgetShell className="flex flex-col w-[clamp(440px,50vw,900px)] max-w-[calc(100vw-2rem)] max-h-[82vh]">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 flex-shrink-0">
        <StatusDot running={s.running > 0} failed={s.failed > 0} />
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-slate-100 leading-tight">
            Publish progress
          </div>
          <div className="text-[10.5px] text-slate-500 tabular-nums">
            <span className="text-accent-300">{s.running} running</span>
            {" · "}
            <span className="text-emerald-300">{s.done} done</span>
            {s.failed > 0 && (
              <>
                {" · "}
                <span className="text-rose-300">{s.failed} failed</span>
              </>
            )}
            {s.unavailable > 0 && (
              <>
                {" · "}
                <span className="text-sky-300">{s.unavailable} removed</span>
              </>
            )}
            {s.cancelled > 0 && (
              <>
                {" · "}
                <span className="text-slate-400">{s.cancelled} cancelled</span>
              </>
            )}
          </div>
        </div>
        {s.settled > 0 && (
          <button
            type="button"
            onClick={clearFinished}
            className="text-[11px] text-slate-400 hover:text-slate-100 px-2 py-1 rounded-md hover:bg-white/10 transition flex-shrink-0"
            title="Remove every finished job from the panel"
          >
            Clear finished
          </button>
        )}
        <IconButton onClick={onCompact} label="Shrink to compact view">
          {ICON_COLLAPSE}
        </IconButton>
        <IconButton onClick={onMinimize} label="Minimize">
          {ICON_MINIMIZE}
        </IconButton>
      </div>

      <div className="h-[3px] bg-white/[0.05] flex-shrink-0">
        <div
          className={`h-full transition-[width] duration-500 ease-out ${
            s.running === 0 && s.failed > 0
              ? "bg-amber-400"
              : s.running === 0
                ? "bg-emerald-400"
                : "bg-accent-400"
          }`}
          style={{ width: `${s.pct}%` }}
        />
      </div>

      <div className="overflow-y-auto flex-1">
        <BatchSection batchList={batchList} />
        <JobSection title="My publishes" kind="own" jobs={own} />
        <JobSection title="Team publishes" kind="foreign" jobs={foreign} />
      </div>
    </WidgetShell>
  );
}

/** Floating publish-progress widget. One launcher the user cycles
 *  through three modes — bubble, compact card, expanded panel — with
 *  the choice persisted across reloads. Own jobs and teammates' jobs
 *  (surfaced by the agency-wide poller) are shown together, split into
 *  sections in the expanded view. Renders nothing when there are no
 *  jobs to show. */
export function FloatingProgressPanel() {
  const { jobs, batches } = usePublishProgress();
  const list = useMemo(() => Object.values(jobs), [jobs]);
  const batchList = useMemo(() => Object.values(batches), [batches]);
  const [mode, setMode] = useState<PanelMode>(loadMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  // Tick once per second while anything is running so each row's
  // elapsed timer updates without re-rendering the SSE handler.
  const anyRunning =
    list.some((j) => j.state === "running") ||
    batchList.some((b) => b.running > 0);
  const [, force] = useState(0);
  useEffect(() => {
    if (!anyRunning) return;
    const id = window.setInterval(() => force((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [anyRunning]);

  const own = useMemo(() => list.filter((j) => !j.isForeign), [list]);
  const foreign = useMemo(() => list.filter((j) => !!j.isForeign), [list]);

  if (own.length === 0 && foreign.length === 0 && batchList.length === 0) {
    return null;
  }

  const stats = statsWithBatches(computeStats(list), batchList);

  return (
    <div className="fixed z-[60] bottom-4 right-4 flex flex-col items-end">
      {mode === "icon" && (
        <IconBubble stats={stats} onOpen={() => setMode("compact")} />
      )}
      {mode === "compact" && (
        <CompactPanel
          own={own}
          foreign={foreign}
          batchList={batchList}
          onMinimize={() => setMode("icon")}
          onExpand={() => setMode("expanded")}
        />
      )}
      {mode === "expanded" && (
        <ExpandedPanel
          own={own}
          foreign={foreign}
          batchList={batchList}
          onMinimize={() => setMode("icon")}
          onCompact={() => setMode("compact")}
        />
      )}
    </div>
  );
}
